import { motion } from 'framer-motion'
import { BanknotesIcon, ArrowDownTrayIcon, CheckCircleIcon, ClockIcon, XCircleIcon, CreditCardIcon } from '@heroicons/react/24/outline'
import { Sparkles } from 'lucide-react'
import { getPlanDetails, isPro as checkIsPro } from '../../config/plans'
import { trackInvoiceDownload, trackManagePayment as trackManagePaymentEvent } from '../../config/analyticsEvents'

const BillingTab = ({ plan = {}, billingData = null, isLoadingBilling = false, onManagePayment = null, isManagingPayment = false }) => {
  // Current plan info from /dashboard API
  const isPro = checkIsPro(plan)
  const planDetails = getPlanDetails(plan?.name)

  // Billing info from /billing API only
  const currentBilling = billingData?.currentPlan || {}
  const billingHistory = billingData?.billingHistory || []

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0)
  }

  // Get status icon and color
  const getStatusDisplay = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'completed':
        return { icon: CheckCircleIcon, color: 'text-green-600', bg: 'bg-green-100', label: 'Paid' }
      case 'pending':
        return { icon: ClockIcon, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Pending' }
      case 'failed':
        return { icon: XCircleIcon, color: 'text-red-600', bg: 'bg-red-100', label: 'Failed' }
      default:
        return { icon: ClockIcon, color: 'text-gray-600', bg: 'bg-gray-100', label: status || 'Unknown' }
    }
  }

  // Get plan display name
  const planDisplayName = planDetails?.name || (isPro ? 'Pro Plan' : 'Free Plan')
  const planDescription = planDetails?.description || ''
  const planFeatures = planDetails?.features || []

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Billing & Subscription</h2>
        <p className="text-gray-600">Manage your subscription and view billing information</p>
      </div>

      {/* Current Plan & Billing Information */}
      <motion.div 
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Current Plan */}
        <div className="relative group">
          {isPro && (
            <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 via-emerald-500 to-primary-500 rounded-xl blur opacity-40 group-hover:opacity-60 transition duration-300 animate-pulse"></div>
          )}
          <div className={`relative rounded-xl shadow-sm ${
            isPro 
              ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' 
              : 'bg-white border border-gray-200'
          }`}>
            <div className={`px-6 py-4 border-b flex items-center justify-between ${
              isPro ? 'border-slate-700' : 'border-gray-200'
            }`}>
              <h3 className={`text-lg font-semibold ${isPro ? 'text-white' : 'text-gray-900'}`}>Current Plan</h3>
              {isPro && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-primary-500 to-emerald-500 text-white">
                  <Sparkles className="w-3 h-3" />
                  PRO
                </span>
              )}
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-2">
                <h4 className={`text-2xl font-bold ${isPro ? 'text-white' : 'text-gray-900'}`}>{planDisplayName}</h4>
              </div>
              <p className={`mb-4 ${isPro ? 'text-slate-400' : 'text-gray-600'}`}>{planDescription}</p>
              {planFeatures.length > 0 && (
                <ul className={`text-sm space-y-1 ${isPro ? 'text-slate-400' : 'text-gray-600'}`}>
                  {planFeatures.map((feature, index) => (
                    <li key={index}>• {feature}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Billing Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Billing Information</h3>
          </div>
          <div className="p-6">
            {isLoadingBilling ? (
              <div className="animate-pulse space-y-3">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-28"></div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-28"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-28"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current plan cost:</span>
                  <span className="font-medium text-gray-900">
                    {currentBilling.cost || formatCurrency(0)}
                  </span>
                </div>
                {/* <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Next billing date:</span>
                  <span className="font-medium text-gray-900">
                    {currentBilling.nextBillingDate ? formatDate(currentBilling.nextBillingDate) : '-'}
                  </span>
                </div> */}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Payment method:</span>
                  <span className="font-medium text-gray-900">
                    {currentBilling.paymentMethod || (isPro ? 'Card on file' : 'None')}
                  </span>
                </div>
                {currentBilling.subscriptionStartDate && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Member since:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(currentBilling.subscriptionStartDate)}
                    </span>
                  </div>
                )}
                {isPro && onManagePayment && (
                  <div className="pt-4 mt-4 border-t border-gray-200">
                    <button
                      onClick={() => { trackManagePaymentEvent(); onManagePayment() }}
                      disabled={isManagingPayment}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
                    >
                      {isManagingPayment ? (
                        <>
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                          Redirecting...
                        </>
                      ) : (
                        <>
                          <CreditCardIcon className="w-4 h-4" />
                          Manage in Stripe
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Billing History */}
      <motion.div 
        className="bg-white rounded-xl shadow-sm border border-gray-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Billing History</h3>
          {billingHistory.length > 0 && (
            <span className="text-sm text-gray-500">{billingHistory.length} invoice(s)</span>
          )}
        </div>
        <div className="p-6">
          {isLoadingBilling ? (
            <div className="animate-pulse overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Description</th>
                    <th className="pb-3 font-medium">Amount</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium text-right">Invoice</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[1, 2, 3].map((i) => (
                    <tr key={i} className="text-sm">
                      <td className="py-3">
                        <div className="h-4 bg-gray-200 rounded w-28"></div>
                      </td>
                      <td className="py-3">
                        <div className="h-4 bg-gray-200 rounded w-36"></div>
                      </td>
                      <td className="py-3">
                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                      </td>
                      <td className="py-3">
                        <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                      </td>
                      <td className="py-3 text-right">
                        <div className="h-4 bg-gray-200 rounded w-20 ml-auto"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : billingHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Description</th>
                    <th className="pb-3 font-medium">Amount</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium text-right">Invoice</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {billingHistory.map((invoice, index) => {
                    const statusDisplay = getStatusDisplay(invoice.status)
                    const StatusIcon = statusDisplay.icon
                    return (
                      <tr key={invoice.id || index} className="text-sm">
                        <td className="py-3 text-gray-900">{formatDate(invoice.transactionDate || invoice.date)}</td>
                        <td className="py-3 text-gray-600">{invoice.description || 'Pro Plan Subscription'}</td>
                        <td className="py-3 text-gray-900 font-medium">{invoice.amount}</td>
                        <td className="py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusDisplay.bg} ${statusDisplay.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusDisplay.label}
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          {invoice.invoiceUrl ? (
                            <a
                              href={invoice.invoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackInvoiceDownload()}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                              Download
                            </a>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BanknotesIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No billing history</h4>
              <p className="text-sm">
                {isPro 
                  ? 'Your invoices will appear here after your first billing cycle.'
                  : 'Your invoices and payment history will appear here once you upgrade to a paid plan.'
                }
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default BillingTab